import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import ContentLoader from "../ContentLoader";
import PdfReportPreviewModal from "../PdfReportPreviewModal";
import RecordDetailModal from "../RecordDetailModal";
import TableSortSelect from "../TableSortSelect";
import { Alert, Button } from "../ui";
import { fetchReports, downloadReport } from "../../services/reportsApi";
import {
  getClientVisibleReports,
  getLatestClientReportDate,
  markReportsViewed,
} from "../../utils/reportReadState";
import { showAlert } from "../AppAlert/appAlert";
import { useMenu } from "../../Common/MenuContext";
import { DEFAULT_TABLE_SORT, sortTableRows } from "../../utils/tableSort";

import "../../styles/client-reports.css";

type ReportRow = {
  id?: number | string;
  reportItemId?: string;
  remarks?: string;
  attachmentUrl?: string;
  status?: string;
  reportDate?: string | null;
  submittedAt?: string | null;
  members?: Array<{ username?: string; jobTitle?: string }>;
};

const extractFileName = (filePath: string | undefined) => {
  if (!filePath) return "";
  const normalized = String(filePath).replace(/\\/g, "/");
  const parts = normalized.split("/");
  return parts[parts.length - 1] || "";
};

const formatReportDate = (dateValue: string | null | undefined) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
  };
  const datePart = new Intl.DateTimeFormat("en-US", options).format(date);
  const timePart = date.toLocaleTimeString("en-US");
  return `${datePart}, ${timePart}`;
};

const membersSummary = (members: ReportRow["members"]) => {
  if (!members?.length) return "";
  return members
    .filter((member) => member.username || member.jobTitle)
    .map((member) => {
      const name = member.username?.trim() || "Member";
      const title = member.jobTitle?.trim();
      return title ? `${name} (${title})` : name;
    })
    .join(" ");
};

const EmptyCell = () => <span className="app-td__empty">—</span>;

export type ClientProjectReportsPanelProps = {
  projectId: string | null;
  userId?: number | string | null;
  /** Super-user portal includes team members on each report. */
  showMembersColumn?: boolean;
};

export default function ClientProjectReportsPanel({
  projectId,
  userId,
  showMembersColumn = false,
}: ClientProjectReportsPanelProps) {
  const { refreshChatCount } = useMenu();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(DEFAULT_TABLE_SORT);

  const [previewReport, setPreviewReport] = useState<ReportRow | null>(null);
  const [viewReport, setViewReport] = useState<ReportRow | null>(null);

  const loadReports = useCallback(async () => {
    if (!projectId) {
      setReports([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchReports(projectId);
      const submitted = getClientVisibleReports(
        Array.isArray(data) ? (data as ReportRow[]) : []
      );
      setReports(submitted);

      if (submitted.length > 0 && userId != null) {
        markReportsViewed(
          userId,
          projectId,
          getLatestClientReportDate(submitted) || undefined
        );
      }

      refreshChatCount();
    } catch {
      setError("Failed to load reports.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, userId, refreshChatCount]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const searchNeedle = searchTerm.trim().toLowerCase();

  const tableRows = useMemo(() => {
    const filtered = searchNeedle
      ? reports.filter((report) => {
          const haystack = [
            report.reportItemId,
            report.remarks,
            extractFileName(report.attachmentUrl),
            membersSummary(report.members),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(searchNeedle);
        })
      : reports;

    return sortTableRows(filtered, sortKey, {
      name: (report: ReportRow) => report.reportItemId || "",
      date: (report: ReportRow) => {
        const raw = report.submittedAt || report.reportDate;
        const ts = Date.parse(raw || "");
        return Number.isNaN(ts) ? 0 : ts;
      },
    });
  }, [reports, searchNeedle, sortKey]);

  const hasAttachment = (report: ReportRow | null) =>
    Boolean(report?.attachmentUrl?.trim());

  const handleDownload = async (report: ReportRow) => {
    if (!projectId || !report.reportItemId) return;
    const fileName = extractFileName(report.attachmentUrl) || "report.pdf";
    try {
      await downloadReport(projectId, report.reportItemId, fileName);
    } catch (err: unknown) {
      console.error(err);
      showAlert({
        type: "error",
        title: "Download failed",
        message: "The file could not be downloaded. Please try again later.",
        confirmText: "OK",
      });
    }
  };

  const renderBody = () => {
    if (!projectId) {
      return (
        <Alert
          variant="warning"
          description="Select a project from the dashboard to view submitted reports."
        />
      );
    }

    if (loading) {
      return <ContentLoader compact label="Loading reports…" />;
    }

    if (error) {
      return <Alert variant="error" description={error} />;
    }

    if (reports.length === 0) {
      return (
        <div className="client-reports__empty">
          <p className="client-reports__empty-title m-0">No reports yet</p>
          <p className="client-reports__empty-hint m-0">
            Your advisor&apos;s evaluation will appear here once it has been submitted.
          </p>
        </div>
      );
    }

    if (tableRows.length === 0) {
      return (
        <div className="client-reports__empty">
          <p className="client-reports__empty-title m-0">No matches</p>
          <p className="client-reports__empty-hint m-0">
            No reports match &ldquo;{searchTerm.trim()}&rdquo;. Try another term or clear the
            search.
          </p>
        </div>
      );
    }

    return (
      <div className="app-table-wrap app-table-wrap--refined client-reports-table dash-table-mobile-skip">
        <table className="table table-striped">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Report ID</th>
              <th scope="col">Remarks</th>
              {showMembersColumn ? <th scope="col">Members</th> : null}
              <th scope="col">Submitted</th>
              <th scope="col">File</th>
              <th scope="col" className="client-reports__actions-col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((report, index) => {
              const fileName = extractFileName(report.attachmentUrl) || "report.pdf";
              const isPdf = /\.pdf$/i.test(fileName);
              const remarks = report.remarks?.trim();
              const membersText = membersSummary(report.members);

              return (
                <tr
                  key={String(report.id ?? report.reportItemId ?? index)}
                  className="rdm-row-clickable"
                  onClick={() => setViewReport(report)}
                >
                  <td>
                    <span className="app-td__index">{index + 1}</span>
                  </td>
                  <td title={report.reportItemId || undefined}>
                    <span className="app-td__title">
                      {report.reportItemId?.trim() || "—"}
                    </span>
                  </td>
                  <td>
                    {remarks ? (
                      <span className="app-td__desc client-reports__remarks">{remarks}</span>
                    ) : (
                      <EmptyCell />
                    )}
                  </td>
                  {showMembersColumn ? (
                    <td>
                      {membersText ? (
                        <span className="app-td__desc client-reports__members">{membersText}</span>
                      ) : (
                        <EmptyCell />
                      )}
                    </td>
                  ) : null}
                  <td>
                    <span className="app-td__meta">
                      {formatReportDate(report.submittedAt || report.reportDate)}
                    </span>
                  </td>
                  <td>
                    {hasAttachment(report) ? (
                      <span className="app-td__meta client-reports__file-name" title={fileName}>
                        {fileName}
                      </span>
                    ) : (
                      <span className="app-td__desc client-reports__no-file">No file</span>
                    )}
                  </td>
                  <td
                    className="client-reports__actions client-reports__actions-col"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {hasAttachment(report) ? (
                      <div className="btn-list-actions client-reports__action-group">
                        {isPdf ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={() => setPreviewReport(report)}
                          >
                            Preview
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => void handleDownload(report)}
                        >
                          Download
                        </Button>
                      </div>
                    ) : (
                      <span className="app-td__desc client-reports__no-file">No attachment</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <section className="client-reports-card" aria-label="Submitted project reports">
        {reports.length > 0 ? (
          <div className="client-reports-card__toolbar-row">
            <p className="client-reports-card__count m-0">
              {tableRows.length === reports.length
                ? `${reports.length} submitted report${reports.length === 1 ? "" : "s"}`
                : `Showing ${tableRows.length} of ${reports.length}`}
            </p>
            <div className="dash-list-toolbar__actions client-reports__toolbar">
              <div className="dash-search-field dash-table-search client-reports__search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="search"
                  autoComplete="off"
                  className="dash-search-field__input"
                  placeholder="Search ID, remarks, or file…"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  aria-label="Search reports"
                />
              </div>
              <TableSortSelect value={sortKey} onChange={setSortKey} />
            </div>
          </div>
        ) : null}

        <div className="client-reports-card__body">{renderBody()}</div>
      </section>

      <RecordDetailModal
        show={Boolean(viewReport)}
        onHide={() => setViewReport(null)}
        title="Report details"
        subtitle={
          viewReport?.submittedAt
            ? `Submitted ${formatReportDate(viewReport.submittedAt)}`
            : viewReport?.reportDate
              ? `Created ${formatReportDate(viewReport.reportDate)}`
              : undefined
        }
        fields={
          viewReport
            ? [
                { label: "Report ID", value: viewReport.reportItemId?.trim() || "—" },
                { label: "Submitted", value: formatReportDate(viewReport.submittedAt) },
                {
                  label: "Remarks",
                  value: viewReport.remarks?.trim() || "—",
                  fullWidth: true,
                },
                ...(showMembersColumn
                  ? [
                      {
                        label: "Members",
                        value: membersSummary(viewReport.members) || "—",
                        fullWidth: true,
                      },
                    ]
                  : []),
                {
                  label: "Attachment",
                  value: hasAttachment(viewReport)
                    ? extractFileName(viewReport.attachmentUrl)
                    : "No attachment",
                },
              ]
            : []
        }
        actions={
          viewReport && hasAttachment(viewReport)
            ? [
                ...(/\.pdf$/i.test(extractFileName(viewReport.attachmentUrl))
                  ? [
                      {
                        type: "preview" as const,
                        onClick: () => {
                          const report = viewReport;
                          setViewReport(null);
                          if (report) setPreviewReport(report);
                        },
                      },
                    ]
                  : []),
                {
                  type: "download" as const,
                  onClick: () => {
                    const report = viewReport;
                    setViewReport(null);
                    if (report) void handleDownload(report);
                  },
                },
              ]
            : []
        }
      />

      <PdfReportPreviewModal
        show={Boolean(previewReport)}
        onHide={() => setPreviewReport(null)}
        title="Capability Assessment Report"
        projectId={projectId ?? undefined}
        reportItemId={previewReport?.reportItemId}
        fileName={extractFileName(previewReport?.attachmentUrl) || "report.pdf"}
        onDownload={
          previewReport ? () => void handleDownload(previewReport) : undefined
        }
      />
    </>
  );
}
